/**
 * Pure geometry for the canvas glyph tracer. No DOM: every helper here runs
 * headless so the tracer's forgiveness rules can be unit-tested in jsdom
 * (which has no canvas) and so the sampler never depends on an SVG element.
 *
 * Coordinates are the glyphStrokes convention: a 1×1 box, y down.
 */

/** Kindergarten paper metrics (fractions of the glyph box). */
export const GUIDE_LINES = Object.freeze({ top: 0.12, mid: 0.42, base: 0.88 });

/** Default forgiveness, tuned for 3–7 year olds on a tablet. */
export const TRACE_RULES = Object.freeze({
  samples: 64,
  tolerance: 0.11,      // band around each sample point, fraction of the glyph box
  freeSkip: 3,          // sample points a wobble may hop over for free
  recoverSkip: 0.25,    // fraction of a stroke the finger may rejoin further along (penalised)
  minCoverage: 0.85,    // share of sample points that must be met
  endTolerance: 0.18,   // how close to the stroke end the finger must lift
  shortStroke: 0.36,    // strokes shorter than this may be traced from either end
  minAttempt: 0.06,     // ink shorter than this is a tap, not an attempt
  dotExtent: 0.1,       // strokes smaller than this are dots: a tap on them passes
});

function dist(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Tokenise an SVG path string into [{ cmd, args[] }]. Supports M L C Q A Z (absolute). */
export function parsePath(d) {
  const out = [];
  const src = String(d || "");
  const re = /([MLCQAZmlcqaz])|(-?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?)/g;
  let cur = null;
  let m;
  while ((m = re.exec(src))) {
    if (m[1]) {
      cur = { cmd: m[1].toUpperCase(), args: [] };
      out.push(cur);
    } else if (cur) {
      cur.args.push(parseFloat(m[2]));
    }
  }
  return out;
}

function cubic(p0, p1, p2, p3, t) {
  const u = 1 - t;
  return {
    x: u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x,
    y: u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y,
  };
}

function quad(p0, p1, p2, t) {
  const u = 1 - t;
  return {
    x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x,
    y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y,
  };
}

function vecAngle(ux, uy, vx, vy) {
  const dot = ux * vx + uy * vy;
  const len = Math.hypot(ux, uy) * Math.hypot(vx, vy);
  let ang = Math.acos(Math.max(-1, Math.min(1, dot / (len || 1))));
  if (ux * vy - uy * vx < 0) ang = -ang;
  return ang;
}

/** SVG arc (endpoint parameterisation) → polyline. Implements spec appendix F.6.5. */
export function flattenArc(p0, rx, ry, rotDeg, largeArc, sweep, p1, steps = 48) {
  if (p0.x === p1.x && p0.y === p1.y) return [];
  rx = Math.abs(rx);
  ry = Math.abs(ry);
  if (rx === 0 || ry === 0) return [p1];
  const phi = (rotDeg * Math.PI) / 180;
  const cosPhi = Math.cos(phi);
  const sinPhi = Math.sin(phi);
  const dx2 = (p0.x - p1.x) / 2;
  const dy2 = (p0.y - p1.y) / 2;
  const x1p = cosPhi * dx2 + sinPhi * dy2;
  const y1p = -sinPhi * dx2 + cosPhi * dy2;
  const lambda = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry);
  if (lambda > 1) {
    rx *= Math.sqrt(lambda);
    ry *= Math.sqrt(lambda);
  }
  const sign = largeArc !== sweep ? 1 : -1;
  const num = rx * rx * ry * ry - rx * rx * y1p * y1p - ry * ry * x1p * x1p;
  const den = rx * rx * y1p * y1p + ry * ry * x1p * x1p;
  const coef = sign * Math.sqrt(Math.max(0, den === 0 ? 0 : num / den));
  const cxp = (coef * rx * y1p) / ry;
  const cyp = (coef * -ry * x1p) / rx;
  const cx = cosPhi * cxp - sinPhi * cyp + (p0.x + p1.x) / 2;
  const cy = sinPhi * cxp + cosPhi * cyp + (p0.y + p1.y) / 2;
  const theta1 = vecAngle(1, 0, (x1p - cxp) / rx, (y1p - cyp) / ry);
  let dTheta = vecAngle((x1p - cxp) / rx, (y1p - cyp) / ry, (-x1p - cxp) / rx, (-y1p - cyp) / ry);
  if (!sweep && dTheta > 0) dTheta -= Math.PI * 2;
  if (sweep && dTheta < 0) dTheta += Math.PI * 2;
  const pts = [];
  const n = Math.max(8, Math.round((Math.abs(dTheta) / (Math.PI * 2)) * steps * 2));
  for (let i = 1; i <= n; i += 1) {
    const th = theta1 + (dTheta * i) / n;
    const ct = Math.cos(th);
    const st = Math.sin(th);
    pts.push({
      x: cx + rx * ct * cosPhi - ry * st * sinPhi,
      y: cy + rx * ct * sinPhi + ry * st * cosPhi,
    });
  }
  pts[pts.length - 1] = { x: p1.x, y: p1.y };
  return pts;
}

/** Flatten a path (M L C Q A Z) into a dense polyline of {x,y}. */
export function flattenPath(d, curveSteps = 40) {
  const segs = parsePath(d);
  const pts = [];
  let cur = null;
  let subStart = null;
  const push = (p) => {
    if (!cur || p.x !== cur.x || p.y !== cur.y) pts.push(p);
    cur = p;
  };
  segs.forEach(({ cmd, args }) => {
    if (cmd === "M") {
      for (let i = 0; i + 1 < args.length; i += 2) {
        const p = { x: args[i], y: args[i + 1] };
        if (i === 0) {
          cur = null;
          push(p);
          subStart = p;
        } else push(p);
      }
    } else if (cmd === "L") {
      for (let i = 0; i + 1 < args.length; i += 2) push({ x: args[i], y: args[i + 1] });
    } else if (cmd === "C" && cur) {
      for (let i = 0; i + 5 < args.length; i += 6) {
        const p0 = cur;
        const p1 = { x: args[i], y: args[i + 1] };
        const p2 = { x: args[i + 2], y: args[i + 3] };
        const p3 = { x: args[i + 4], y: args[i + 5] };
        for (let s = 1; s <= curveSteps; s += 1) push(cubic(p0, p1, p2, p3, s / curveSteps));
      }
    } else if (cmd === "Q" && cur) {
      for (let i = 0; i + 3 < args.length; i += 4) {
        const p0 = cur;
        const p1 = { x: args[i], y: args[i + 1] };
        const p2 = { x: args[i + 2], y: args[i + 3] };
        for (let s = 1; s <= curveSteps; s += 1) push(quad(p0, p1, p2, s / curveSteps));
      }
    } else if (cmd === "A" && cur) {
      for (let i = 0; i + 6 < args.length; i += 7) {
        const end = { x: args[i + 5], y: args[i + 6] };
        flattenArc(cur, args[i], args[i + 1], args[i + 2], !!args[i + 3], !!args[i + 4], end, curveSteps).forEach(push);
      }
    } else if (cmd === "Z" && subStart) {
      push({ x: subStart.x, y: subStart.y });
    }
  });
  return pts;
}

/** Total length of a polyline. */
export function polylineLength(pts) {
  let len = 0;
  for (let i = 1; i < pts.length; i += 1) len += dist(pts[i - 1], pts[i]);
  return len;
}

/** Resample a polyline into `n` points spaced evenly by arc length (first and last kept). */
export function resample(pts, n) {
  if (!pts.length) return [];
  if (pts.length === 1 || n <= 1) return [pts[0]];
  const cum = [0];
  for (let i = 1; i < pts.length; i += 1) cum.push(cum[i - 1] + dist(pts[i - 1], pts[i]));
  const total = cum[cum.length - 1];
  if (total === 0) return Array.from({ length: n }, () => ({ ...pts[0] }));
  const out = [];
  let seg = 1;
  for (let k = 0; k < n; k += 1) {
    const target = (total * k) / (n - 1);
    while (seg < cum.length - 1 && cum[seg] < target) seg += 1;
    const a = pts[seg - 1];
    const b = pts[seg];
    const span = cum[seg] - cum[seg - 1];
    const t = span === 0 ? 0 : (target - cum[seg - 1]) / span;
    out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
  }
  return out;
}

/** Point + unit tangent at a fraction `t` (0..1) of a polyline's length. */
export function pointAlong(pts, t) {
  if (!pts.length) return { x: 0, y: 0, dx: 1, dy: 0 };
  if (pts.length === 1) return { ...pts[0], dx: 1, dy: 0 };
  const total = polylineLength(pts);
  let target = Math.max(0, Math.min(1, t)) * total;
  for (let i = 1; i < pts.length; i += 1) {
    const a = pts[i - 1];
    const b = pts[i];
    const span = dist(a, b);
    if (target <= span || i === pts.length - 1) {
      const u = span === 0 ? 0 : Math.max(0, Math.min(1, target / span));
      const dx = span === 0 ? 1 : (b.x - a.x) / span;
      const dy = span === 0 ? 0 : (b.y - a.y) / span;
      return { x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u, dx, dy };
    }
    target -= span;
  }
  const last = pts[pts.length - 1];
  return { ...last, dx: 1, dy: 0 };
}

/** Sample one SVG stroke path into `n` evenly spaced points plus a dense polyline for rendering. */
export function samplePath(d, n = TRACE_RULES.samples) {
  const poly = flattenPath(d);
  const points = resample(poly, n);
  return { poly, points, length: polylineLength(poly) };
}

/**
 * Prepare a glyph's strokes ({ d, start, prompt }[]) for tracing.
 * Returns [{ d, prompt, start, end, points, poly, length, short }].
 */
export function sampleGlyphStrokes(strokes, n = TRACE_RULES.samples) {
  return (strokes || [])
    .filter((s) => s && typeof s.d === "string" && s.d.trim())
    .map((s, i) => {
      const { poly, points, length } = samplePath(s.d, n);
      const start = s.start ? { x: s.start[0], y: s.start[1] } : points[0];
      const xs = poly.map((p) => p.x);
      const ys = poly.map((p) => p.y);
      const extent = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
      const center = { x: (Math.max(...xs) + Math.min(...xs)) / 2, y: (Math.max(...ys) + Math.min(...ys)) / 2 };
      return {
        d: s.d,
        prompt: s.prompt || `Stroke ${i + 1}`,
        start,
        end: points[points.length - 1] || start,
        points,
        poly,
        even: resample(poly, 160), // evenly spaced by length, for partial (demo) rendering
        length,
        extent,
        center,
        short: length < TRACE_RULES.shortStroke,
        dot: extent < TRACE_RULES.dotExtent, // the dot on i / j: a tap counts
      };
    })
    .filter((s) => s.points.length > 1);
}

/** Legacy fallback: per-config point arrays ([{x,y}][]) → { d, start, prompt } strokes. */
export function strokesFromPoints(pointStrokes, prompts = []) {
  return (pointStrokes || [])
    .filter((pts) => Array.isArray(pts) && pts.length > 1)
    .map((pts, i) => ({
      d: pts.map((p, j) => `${j === 0 ? "M" : "L"} ${p.x},${p.y}`).join(" "),
      start: [pts[0].x, pts[0].y],
      prompt: prompts[i] || `Stroke ${i + 1}`,
    }));
}

/**
 * Where the 1×1 glyph box sits on a paper of w×h px: the largest square that
 * fits with a margin, centred. Pure so pointer mapping and drawing agree.
 */
export function glyphBoxFor(w, h, margin = 0.08) {
  const side = Math.max(1, Math.min(w, h));
  const pad = side * margin;
  const s = Math.max(1, side - pad * 2);
  return { x: (w - s) / 2, y: (h - s) / 2, s };
}

/**
 * Ordered checkpoint tracker for one stroke.
 *   feed(p)      — advance through sample points near p (normalised coords)
 *   evaluate(end)— { passed, coverage, endOk }
 * Skips of up to `freeSkip` points are forgiven (counted met); the finger may
 * rejoin the path up to `recoverSkip` of the stroke further along, but those
 * points stay unmet and count against coverage.
 */
export function createStrokeTracker(points, rules = {}) {
  const r = { ...TRACE_RULES, ...rules };
  const n = points.length;
  const met = new Array(n).fill(false);
  const recover = Math.max(r.freeSkip, Math.round(n * r.recoverSkip));
  let next = 0;

  const feed = (p) => {
    let advanced = false;
    let guard = 0;
    while (next < n && guard < n) {
      guard += 1;
      let hit = -1;
      for (let k = 0; k <= recover && next + k < n; k += 1) {
        if (dist(p, points[next + k]) <= r.tolerance) {
          hit = next + k;
          break;
        }
      }
      if (hit < 0) break;
      const hop = hit - next;
      if (hop <= r.freeSkip) for (let i = next; i <= hit; i += 1) met[i] = true;
      else met[hit] = true;
      next = hit + 1;
      advanced = true;
    }
    return advanced;
  };

  const evaluate = (endPoint) => {
    const metCount = met.reduce((a, b) => a + (b ? 1 : 0), 0);
    const coverage = n ? metCount / n : 0;
    const endOk = endPoint ? dist(endPoint, points[n - 1]) <= r.endTolerance : next >= n;
    return { passed: coverage >= r.minCoverage && endOk, coverage, endOk };
  };

  return {
    feed,
    evaluate,
    get progress() {
      return n ? next / n : 0;
    },
    get met() {
      return met.slice();
    },
    reset() {
      met.fill(false);
      next = 0;
    },
  };
}

/**
 * Judge a whole attempt (array of normalised ink points) against a sampled
 * stroke. Short strokes may be drawn from either end. Returns
 * { passed, coverage, reversed, attempt } where attempt=false means the ink was
 * too small to count as a try (a tap).
 */
export function judgeAttempt(stroke, ink, rules = {}) {
  const r = { ...TRACE_RULES, ...rules };
  if (!ink || !ink.length) return { passed: false, coverage: 0, reversed: false, attempt: false };
  if (stroke.dot) {
    const c = stroke.center || stroke.start;
    const hit = ink.some((p) => dist(p, c) <= r.tolerance * 1.4);
    return { passed: hit, coverage: hit ? 1 : 0, reversed: false, attempt: true };
  }
  if (ink.length < 2) return { passed: false, coverage: 0, reversed: false, attempt: false };
  const inkLength = polylineLength(ink);
  if (inkLength < r.minAttempt) return { passed: false, coverage: 0, reversed: false, attempt: false };
  const last = ink[ink.length - 1];
  const fwd = createStrokeTracker(stroke.points, r);
  ink.forEach((p) => fwd.feed(p));
  const f = fwd.evaluate(last);
  if (f.passed || !stroke.short) return { ...f, reversed: false, attempt: true };
  const rev = createStrokeTracker(stroke.points.slice().reverse(), r);
  ink.forEach((p) => rev.feed(p));
  const b = rev.evaluate(last);
  if (b.passed) return { ...b, reversed: true, attempt: true };
  return { ...(f.coverage >= b.coverage ? f : b), reversed: false, attempt: true };
}

/** Mix a hex colour toward another (t=0 → a, t=1 → b). */
export function mixHex(a, b, t) {
  const parse = (hex) => {
    const raw = String(hex || "#000000").replace("#", "");
    const full = raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw.padEnd(6, "0");
    const n = parseInt(full.slice(0, 6), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  };
  const A = parse(a);
  const B = parse(b);
  const ch = (i) => Math.round(A[i] + (B[i] - A[i]) * t).toString(16).padStart(2, "0");
  return `#${ch(0)}${ch(1)}${ch(2)}`;
}

/** Ink width factor from speed (px/ms): slow = fat pressed crayon, fast = lighter. */
export function crayonWidthFactor(speedPxPerMs, prev = 1) {
  const target = Math.max(0.72, Math.min(1.18, 1.18 - speedPxPerMs * 0.32));
  return prev + (target - prev) * 0.35;
}
